import { Box, Collapse, HStack, Icon, Stack, Text, useDisclosure } from '@chakra-ui/react';
import { djs } from '@duke-hq/djs';
import { RouterOutputs } from '@duke-hq/svc-api/admin';
import { StudentEmployerPlacementStatus } from '@duke-hq/svc-api-db/client';
import { iconSize } from '@duke-hq/ui/constants/scalable';
import { EmployerPlacementStatusBadge } from '@duke-hq/ui/components/PlacementStatusBadge/PlacementStatusBadge';
import { t } from '@lingui/core/macro';
import { Plural, Trans } from '@lingui/react/macro';
import { ChevronDown } from 'lucide-react';
import { hasPlacementAgreement, PlacementAgreementLinks } from './PlacementAgreementLinks';
import { testIds } from './testIds';

type Placement =
  RouterOutputs['workPlacements']['placements']['journeyByEnrolment']['placements'][number];

function formatDate(date: Date | null): string {
  return date != null ? djs(date).format('DD MMM YYYY') : '—';
}

function endedLabel(placement: Placement): string | null {
  const { status } = placement;
  if (
    status !== StudentEmployerPlacementStatus.CANCELLED &&
    status !== StudentEmployerPlacementStatus.FAILED
  ) {
    return null;
  }
  const startedOn = placement.startedAt != null ? formatDate(placement.startedAt) : null;
  if (placement.endedAt == null) {
    return startedOn != null ? t`Start date ${startedOn}` : null;
  }
  const endedOn = formatDate(placement.endedAt);
  const endedBeforeStart = placement.startedAt != null && placement.endedAt < placement.startedAt;
  if (status === StudentEmployerPlacementStatus.CANCELLED) {
    if (startedOn == null) {
      return t`Cancelled ${endedOn}`;
    }
    return endedBeforeStart
      ? t`Due to start ${startedOn} · Cancelled ${endedOn}`
      : t`Started ${startedOn} · Cancelled ${endedOn}`;
  }
  if (startedOn == null) {
    return t`Failed ${endedOn}`;
  }
  return endedBeforeStart
    ? t`Due to start ${startedOn} · Failed ${endedOn}`
    : t`Started ${startedOn} · Failed ${endedOn}`;
}

function dateRange(placement: Placement): string {
  const ended = endedLabel(placement);
  if (ended != null) {
    return ended;
  }
  if (placement.startedAt == null) {
    return djs(placement.createdAt).format('DD MMM YYYY');
  }
  return `${formatDate(placement.startedAt)} – ${formatDate(placement.endedAt)}`;
}

function FieldLabel({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <Text fontSize="xs" color="fg.muted" mb={1}>
      {children}
    </Text>
  );
}

function HistoryItem({ placement }: { placement: Placement }): React.ReactElement {
  const { isOpen, onToggle } = useDisclosure();
  const provider = placement.employer?.name ?? null;
  const completedUnits = placement.units.filter((unit) => unit.completedAt != null);

  return (
    <Box
      borderWidth="1px"
      borderColor="border.default"
      borderRadius="xl"
      overflow="hidden"
      data-testid={testIds.historyItem}
    >
      <HStack
        as="button"
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        w="full"
        justify="space-between"
        gap={3}
        px={4}
        py={3}
        _hover={{ bg: 'bg.subtle' }}
      >
        <HStack gap={3} minW={0}>
          <EmployerPlacementStatusBadge status={placement.status} />
          <Text fontSize="sm" color="fg.muted" noOfLines={1} flexShrink={0}>
            {dateRange(placement)}
          </Text>
          {provider != null ? (
            <Text fontSize="sm" color="fg.subtle" noOfLines={1}>
              {provider}
            </Text>
          ) : null}
        </HStack>
        <Icon
          as={ChevronDown}
          boxSize={iconSize.md}
          color="fg.subtle"
          flexShrink={0}
          transform={isOpen ? undefined : 'rotate(-90deg)'}
          transition="transform 0.15s ease"
        />
      </HStack>

      <Collapse in={isOpen} animateOpacity>
        <Stack gap={4} bg="bg.subtle" p={4} borderTopWidth="1px" borderColor="border.default">
          {hasPlacementAgreement(placement) ? (
            <Box>
              <FieldLabel>
                <Trans>Workplace agreement</Trans>
              </FieldLabel>
              <PlacementAgreementLinks placement={placement} />
            </Box>
          ) : null}

          {placement.notes != null && placement.notes !== '' ? (
            <Box>
              <FieldLabel>
                <Trans>Notes</Trans>
              </FieldLabel>
              <Text fontSize="sm">{placement.notes}</Text>
            </Box>
          ) : null}

          {completedUnits.length > 0 ? (
            <Box>
              <FieldLabel>
                <Trans>Units with work placement completed</Trans>
              </FieldLabel>
              <Stack gap={1}>
                {completedUnits.map((unit) => (
                  <Text key={unit.unitOfCompetencyId} fontSize="sm" noOfLines={1}>
                    <Text as="span" fontWeight="medium">
                      {unit.code}
                    </Text>{' '}
                    · {unit.title}
                  </Text>
                ))}
              </Stack>
            </Box>
          ) : null}
        </Stack>
      </Collapse>
    </Box>
  );
}

export function PlacementHistorySection({
  placements,
}: {
  placements: Placement[];
}): React.ReactElement | null {
  const { isOpen, onToggle } = useDisclosure();

  if (placements.length === 0) {
    return null;
  }

  return (
    <Stack
      bg="bg.surface"
      borderWidth="1px"
      borderColor="border.default"
      borderRadius="2xl"
      p={5}
      gap={4}
      data-testid={testIds.placementHistorySection}
    >
      <HStack
        as="button"
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        justify="space-between"
        gap={2}
      >
        <HStack gap={2}>
          <Icon
            as={ChevronDown}
            boxSize={iconSize.md}
            color="fg.muted"
            transform={isOpen ? undefined : 'rotate(-90deg)'}
            transition="transform 0.15s ease"
          />
          <Text fontSize="lg" fontWeight="semibold">
            <Trans>Placement history</Trans>
          </Text>
        </HStack>
        <Text fontSize="sm" color="fg.muted">
          <Plural value={placements.length} one="# past placement" other="# past placements" />
        </Text>
      </HStack>

      <Collapse in={isOpen} animateOpacity>
        <Stack gap={3}>
          {placements.map((placement) => (
            <HistoryItem key={placement.id} placement={placement} />
          ))}
        </Stack>
      </Collapse>
    </Stack>
  );
}
